/**
 * Detail drawer for one risk-register item (Analysis tab).
 *
 * Deliberately the same shape as `RequirementDrawer`: a plain <aside> beside the
 * table on desktop (>=lg) so the rows stay clickable, and `ui/sheet.tsx` below
 * that, where the two-column layout does not exist. Head / scrolling body /
 * pinned composer footer, and the same `w-28` label column.
 *
 * This is where a risk is worked, not just read: the mitigation plan, the
 * residual severity left after it, and the team's decision all live here. The
 * extracted facts themselves (title, description, clause) stay read-only —
 * correcting an extraction is a separate job.
 */

import { useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { CitationLink } from '@/components/extraction/CitationLink'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { RequirementDiscussion } from '@/components/extraction/RequirementDiscussion'
import { StatusDropdown, RISK_STATUS_OPTIONS } from '@/components/extraction/StatusDropdown'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { useExtractionStore } from '@/stores/extraction-store'
import { cn } from '@/lib/utils'
import {
  ALL_RISK_DECISIONS,
  RISK_DECISION_LABELS,
  RISK_IMPACT_AREA_LABELS,
  RISK_SEVERITY_COLORS,
  RISK_SEVERITY_LABELS,
  type ExtractedRiskItem,
  type RiskDecision,
  type RiskSeverity,
} from '@/types/extraction'

// ===================
// Constants
// ===================

/**
 * Radix rejects an empty-string `SelectItem` value, so "unset" travels as a
 * sentinel and is mapped back to `null` at the boundary.
 */
const NONE = '__none__'

const SEVERITIES = Object.keys(RISK_SEVERITY_LABELS) as RiskSeverity[]

// ===================
// Types
// ===================

export interface RiskDrawerProps {
  bidId: string
  /** `null` renders nothing — the host keeps the drawer mounted and passes the active row. */
  item: ExtractedRiskItem | null
  onClose: () => void
  readOnly: boolean
  teamMembers: Array<{ id: string; userId: string; fullName: string | null; avatarUrl: string | null }>
  onOwnerChange: (itemId: string, userId: string | null) => void
  onStatusChange: (itemId: string, status: string) => void
}

// ===================
// Pieces
// ===================

/**
 * Exported because the register's Severity column shows the same
 * `Inherent → Residual` pair, read-only. One pill, two surfaces — a second copy
 * in the table would drift from this one the first time a hue changed.
 */
export function SeverityPill({ severity, title }: { severity: RiskSeverity; title: string }) {
  return (
    <Badge
      variant="outline"
      dot={false}
      title={title}
      className={cn('px-1.5 py-0 text-xs font-normal', RISK_SEVERITY_COLORS[severity])}
    >
      {RISK_SEVERITY_LABELS[severity]}
    </Badge>
  )
}

function LabelledRow({
  label,
  align = 'center',
  children,
}: {
  label: string
  align?: 'center' | 'start'
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex gap-3', align === 'center' ? 'items-center' : 'items-start')}>
      <span className={cn('w-28 shrink-0 text-meta', align === 'start' && 'pt-0.5')}>{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

interface MitigationPlanProps {
  plan: string | null
  readOnly: boolean
  onCommit: (plan: string | null) => void
}

/**
 * Click-to-edit plan. Blur and Cmd/Ctrl-Enter commit; Escape discards.
 *
 * Escape unmounts the textarea, and React fires no blur for an element that has
 * been removed — but browsers differ enough on that to be worth a guard, so the
 * cancel flag is checked on commit and reset when editing next opens.
 */
function MitigationPlan({ plan, readOnly, onCommit }: MitigationPlanProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const cancelled = useRef(false)

  if (readOnly || !editing) {
    const body = plan ?? 'No mitigation plan yet.'
    if (readOnly) {
      return <p className="whitespace-pre-wrap text-sm text-muted-foreground">{body}</p>
    }
    return (
      <button
        type="button"
        className="w-full rounded-lg px-1.5 py-1 text-left text-sm hover:bg-accent"
        onClick={() => {
          cancelled.current = false
          setDraft(plan ?? '')
          setEditing(true)
        }}
      >
        <span className={cn('whitespace-pre-wrap', plan ? 'text-foreground' : 'text-subtle')}>
          {plan ?? 'Add a mitigation plan'}
        </span>
      </button>
    )
  }

  const commit = () => {
    setEditing(false)
    if (cancelled.current) return
    const next = draft.trim() || null
    if (next === plan) return
    onCommit(next)
  }

  return (
    <Textarea
      aria-label="Mitigation plan"
      autoFocus
      value={draft}
      className="min-h-[72px] text-sm"
      placeholder="What will the team do about this risk?"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          cancelled.current = true
          setEditing(false)
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          commit()
        }
      }}
    />
  )
}

// ===================
// Body
// ===================

interface DrawerBodyProps {
  bidId: string
  item: ExtractedRiskItem
  onClose: () => void
  readOnly: boolean
  teamMembers: RiskDrawerProps['teamMembers']
  onOwnerChange: RiskDrawerProps['onOwnerChange']
  onStatusChange: RiskDrawerProps['onStatusChange']
  /** The sheet needs a Radix <SheetTitle> for its dialog label; the aside uses a plain <h2>. */
  titleAs?: 'h2' | 'sheet'
}

function DrawerBody({
  bidId,
  item,
  onClose,
  readOnly,
  teamMembers,
  onOwnerChange,
  onStatusChange,
  titleAs = 'h2',
}: DrawerBodyProps) {
  const bulkPatchItems = useExtractionStore((s) => s.bulkPatchItems)
  const updateItemSeverity = useExtractionStore((s) => s.updateItemSeverity)

  /**
   * The pinned footer the discussion portals its composer into. Held as state
   * rather than a ref so the child re-renders once the element exists.
   */
  const [composerSlot, setComposerSlot] = useState<HTMLDivElement | null>(null)

  const itemId = item.id
  const patch = (fields: Record<string, unknown>) => {
    if (!itemId) return
    void bulkPatchItems(bidId, 'risk_register', [itemId], fields)
  }

  const residual = item.residualSeverity ?? null
  const decision = item.riskDecision ?? null
  const statusOption =
    RISK_STATUS_OPTIONS.find((o) => o.value === (item.status || 'identified')) ??
    RISK_STATUS_OPTIONS[0]

  const titleClassName = 'text-sm font-medium'
  const title =
    titleAs === 'sheet' ? (
      <SheetTitle className={titleClassName}>{item.title}</SheetTitle>
    ) : (
      <h2 className={titleClassName}>{item.title}</h2>
    )

  return (
    <>
      {/* Head — fixed */}
      <div className="flex items-start justify-between gap-2 border-b border-border p-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-start gap-1.5">
            <div
              data-testid="risk-severity-pills"
              className="mt-0.5 flex shrink-0 items-center gap-1"
            >
              <SeverityPill severity={item.severity} title="Inherent severity" />
              {residual && (
                <>
                  <span aria-hidden="true" className="text-meta">
                    →
                  </span>
                  <SeverityPill severity={residual} title="Residual severity" />
                </>
              )}
            </div>
            {title}
          </div>
          <p className="text-meta">
            {item.category}
            {item.contractClause ? ` · ${item.contractClause}` : ''}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Close details"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — scrolls */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {/* Full width, NOT behind the w-28 label column. That column earns its
            place here — six real controls line up in it — but a paragraph still
            does not belong in it: indenting one cut the measure to ~250px of a
            400px panel. Same shape as RequirementDrawer: prose, then controls. */}
        {item.description && (
          <p className="text-sm text-muted-foreground">{item.description}</p>
        )}

        <div className="space-y-2.5">
          <LabelledRow label="Impact">
            <span className="text-sm">
              {RISK_IMPACT_AREA_LABELS[item.impactArea] ?? item.impactArea}
            </span>
          </LabelledRow>

          <LabelledRow label="Inherent">
            {readOnly ? (
              <SeverityPill severity={item.severity} title="Inherent severity" />
            ) : (
              <Select
                value={item.severity}
                onValueChange={(value) => {
                  if (itemId) void updateItemSeverity(bidId, itemId, value as RiskSeverity)
                }}
              >
                <SelectTrigger aria-label="Inherent severity" className="h-7 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {RISK_SEVERITY_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </LabelledRow>

          <LabelledRow label="Residual" align="start">
            <div className="space-y-1">
              {readOnly ? (
                residual ? (
                  <SeverityPill severity={residual} title="Residual severity" />
                ) : (
                  <span className="text-sm text-muted-foreground">Not set</span>
                )
              ) : (
                <Select
                  value={residual ?? NONE}
                  onValueChange={(value) =>
                    patch({ residualSeverity: value === NONE ? null : (value as RiskSeverity) })
                  }
                >
                  <SelectTrigger aria-label="Residual severity" className="h-7 w-[140px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {RISK_SEVERITY_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!residual && (
                <p className="text-meta">Set after the mitigation plan is agreed.</p>
              )}
            </div>
          </LabelledRow>

          <LabelledRow label="Decision">
            {readOnly ? (
              <span className="text-sm">
                {decision ? RISK_DECISION_LABELS[decision] : 'No decision'}
              </span>
            ) : (
              <Select
                value={decision ?? NONE}
                onValueChange={(value) =>
                  patch({ riskDecision: value === NONE ? null : (value as RiskDecision) })
                }
              >
                <SelectTrigger aria-label="Decision" className="h-7 w-[180px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No decision</SelectItem>
                  {ALL_RISK_DECISIONS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {RISK_DECISION_LABELS[d]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </LabelledRow>

          <LabelledRow label="Status">
            {readOnly ? (
              statusOption && (
                <Badge variant={statusOption.variant} dot className="text-xs font-normal">
                  {statusOption.label}
                </Badge>
              )
            ) : (
              <StatusDropdown
                value={item.status || 'identified'}
                options={RISK_STATUS_OPTIONS}
                onChange={(status) => itemId && onStatusChange(itemId, status)}
              />
            )}
          </LabelledRow>

          <LabelledRow label="Owner">
            <OwnerPicker
              value={item.ownerId}
              teamMembers={teamMembers}
              disabled={readOnly}
              onChange={(userId) => itemId && onOwnerChange(itemId, userId)}
            />
          </LabelledRow>
        </div>

        {/* Mitigation — the plan the team owns, with the AI's suggestion kept
            beside it. With no plan written yet the suggestion leads, because it
            is the only content there is. */}
        <div className="space-y-2 border-t border-border pt-3">
          <h3 className="text-sm font-medium">Mitigation plan</h3>
          {!item.mitigationPlan && item.mitigation && (
            <AiSuggestion text={item.mitigation} />
          )}
          <MitigationPlan
            plan={item.mitigationPlan ?? null}
            readOnly={readOnly}
            onCommit={(plan) => patch({ mitigationPlan: plan })}
          />
          {item.mitigationPlan && item.mitigation && <AiSuggestion text={item.mitigation} />}
        </div>

        {/* Source */}
        {item.sourceQuote && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-sm font-medium text-foreground">Source</span>
            <blockquote className="border-l-2 border-border pl-2.5 text-xs text-muted-foreground">
              &ldquo;{item.sourceQuote}&rdquo;
            </blockquote>
            <CitationLink
              page={item.sourcePage}
              documentId={item.sourceDocumentId ?? null}
              documentName={item.sourceDocument}
              sourceQuote={item.sourceQuote}
              sourceHighlightData={item.sourceHighlightData ?? null}
            />
          </div>
        )}

        {/* Discussion — a manual row that has never been saved has no id to
            hang a thread off yet. */}
        {itemId && (
          <div className="mt-2 pt-1">
            <RequirementDiscussion
              bidId={bidId}
              itemId={itemId}
              targetType="risk"
              readOnly={readOnly}
              composerSlot={composerSlot}
            />
          </div>
        )}
      </div>

      {/* Pinned composer footer — filled by RequirementDiscussion's portal, and
          collapsed by `empty:hidden` when the viewer may not post. */}
      {itemId && (
        <div ref={setComposerSlot} className="shrink-0 border-t border-border p-3 empty:hidden" />
      )}
    </>
  )
}

/**
 * A full-bleed tinted BAND, not a card. The drawer is already a card and the
 * mitigation section is already inside it, so a rounded bordered box here was a
 * box in a box — the same thing that was fixed on the compliance drawer's AI
 * assessment. The negative margin cancels the body's p-4 so the wash runs edge
 * to edge, and the tint alone does the separating: tinted is the model's
 * suggestion, white is the plan the team owns.
 *
 * `text-sm`, not `text-xs`: this sat a size below the description above it.
 */
function AiSuggestion({ text }: { text: string }) {
  return (
    <div className="-mx-4 space-y-0.5 bg-muted/60 px-4 py-2.5">
      <span className="text-meta">AI’s suggestion</span>
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  )
}

// ===================
// Component
// ===================

export function RiskDrawer({
  bidId,
  item,
  onClose,
  readOnly,
  teamMembers,
  onOwnerChange,
  onStatusChange,
}: RiskDrawerProps) {
  const isDesktop = useIsDesktop()

  if (!item) return null

  const shared = { bidId, item, onClose, readOnly, teamMembers, onOwnerChange, onStatusChange }

  if (!isDesktop) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" hideClose className="flex w-full flex-col p-0 sm:max-w-md">
          <DrawerBody {...shared} titleAs="sheet" />
        </SheetContent>
      </Sheet>
    )
  }

  // Width grows with the screen instead of sitting at a fixed 400px.
  // Floor 400px: at a 1200px viewport the table is ALREADY clipped (needs
  // ~756px, has ~667px), so there is nothing spare to hand over — the clamp
  // must never narrow the table further than it is today.
  // Cap 520px: past that the description paragraph runs longer than a
  // comfortable reading measure.
  return (
    <aside
      role="complementary"
      aria-label="Risk details"
      className="flex max-h-full w-[clamp(400px,26vw,520px)] min-w-0 max-w-full shrink flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm"
    >
      <DrawerBody {...shared} />
    </aside>
  )
}
